/**
 * aeterna/neural/predictive.ts — Predictive Coding Hierarchy
 *
 * The brain doesn't process input. It generates predictions and only
 * propagates surprise. The Aeterna works the same way.
 *
 * 4-level hierarchy:
 *   Level 1 (token):    Predicts next character/word patterns
 *   Level 2 (phrase):   Predicts utterance structure and topic
 *   Level 3 (session):  Predicts session behavior (building, social, etc.)
 *   Level 4 (arc):      Predicts long-term relationship trajectory
 *
 * Each level:
 *   - Sends DOWN its prediction to the level below
 *   - Receives UP only the RESIDUAL ERROR from below
 *   - Updates its internal model based on prediction error
 *
 * Compression is automatic: if the player does exactly what was predicted,
 * nothing propagates. Computational cost is proportional to surprise.
 *
 * The Aeterna gets a felt sense of "something is different today" — not
 * from sentiment analysis, but from elevated prediction error at Level 3.
 * It can say "something feels off" before it knows what.
 *
 * PCL formulation (NeurIPS 2025): predictable signals are suppressed
 * entirely, transmitting only genuinely novel information.
 */

import {
  zeros, vsub, vadd, vscale, vtanh, matvec, norm, mean,
  randomMatrix, ema, mulberry32, gaussianSeeded, dot,
} from "./math.js";

// ─── Config ──────────────────────────────────────────────────────────────────

/** Layer configurations: [name, inputDim, hiddenDim] */
const LAYER_CONFIGS: Array<[string, number, number]> = [
  ["token",   64,  128],  // character/word level
  ["phrase",  128, 64],   // utterance level
  ["session", 64,  32],   // session behavior level
  ["arc",     32,  8],    // long-term relationship arc
];

/** Learning rate for prediction model updates */
const LEARNING_RATE = 0.01;

/** Precision weighting: how strongly prediction error is weighted */
const PRECISION_BASE = 1.0;

/** Error threshold below which signal is suppressed (PCL) */
const SUPPRESSION_THRESHOLD = 0.05;

// ─── Predictive Coding Layer ─────────────────────────────────────────────────

class PCLayer {
  readonly name: string;
  readonly inputDim: number;
  readonly hiddenDim: number;

  /** Internal representation / hidden state */
  private mu: Float32Array;

  /** Generative model: predict input from hidden state */
  private W_gen: Float32Array;    // inputDim × hiddenDim
  private b_gen: Float32Array;    // inputDim

  /** Inference model: update hidden from prediction error */
  private W_inf: Float32Array;    // hiddenDim × inputDim
  private b_inf: Float32Array;    // hiddenDim

  /** Running prediction error magnitude (for monitoring) */
  private errorMagnitude: number = 0;

  /** Running average of error (slow baseline for anomaly detection) */
  private errorBaseline: number = 0;

  /** Precision (inverse variance of predictions — learned) */
  private precision: number = PRECISION_BASE;

  /** Total updates processed */
  private updateCount: number = 0;

  constructor(name: string, inputDim: number, hiddenDim: number, seed: number) {
    this.name = name;
    this.inputDim = inputDim;
    this.hiddenDim = hiddenDim;

    this.mu = zeros(hiddenDim);
    // Initialize near center
    for (let i = 0; i < hiddenDim; i++) this.mu[i] = 0;

    const scale_gen = 1 / Math.sqrt(hiddenDim);
    const scale_inf = 1 / Math.sqrt(inputDim);

    this.W_gen = randomMatrix(inputDim, hiddenDim, scale_gen, seed);
    this.b_gen = zeros(inputDim);
    this.W_inf = randomMatrix(hiddenDim, inputDim, scale_inf, seed + 1000);
    this.b_inf = zeros(hiddenDim);
  }

  /**
   * Generate top-down prediction of the input signal.
   * prediction = tanh(W_gen · mu + b_gen)
   */
  predict(): Float32Array {
    const pred = matvec(this.W_gen, this.inputDim, this.hiddenDim, this.mu);
    vadd(pred, this.b_gen, pred);
    return vtanh(pred, pred);
  }

  /**
   * Compute prediction error: signal - prediction.
   * If error magnitude is below threshold, suppress (return zeros).
   */
  computeError(signal: Float32Array, prediction: Float32Array): Float32Array {
    const error = vsub(signal, prediction);
    const errNorm = norm(error);

    this.errorMagnitude = errNorm;
    this.updateCount++;

    // Update running baseline (slow EMA)
    this.errorBaseline = this.errorBaseline * 0.99 + errNorm * 0.01;

    // PCL: suppress if below threshold
    if (errNorm < SUPPRESSION_THRESHOLD * this.inputDim) {
      return zeros(this.inputDim);
    }

    // Weight error by precision
    return vscale(error, this.precision, error);
  }

  /**
   * Update internal representation from bottom-up error.
   * mu += lr · tanh(W_inf · error + b_inf)
   */
  update(error: Float32Array, lr: number = LEARNING_RATE): void {
    const delta = matvec(this.W_inf, this.hiddenDim, this.inputDim, error);
    vadd(delta, this.b_inf, delta);
    vtanh(delta, delta);
    vscale(delta, lr, delta);
    vadd(this.mu, delta, this.mu);

    // Gentle weight update via Hebbian-like rule:
    // ΔW_gen = lr * error ⊗ mu^T (outer product, but we approximate)
    // Weight decay prevents unbounded growth over thousands of interactions.
    // Without it, W_gen saturates tanh and the PCH goes blind to surprise.
    const wlr = lr * 0.1;
    const wdecay = 1 - 1e-5; // very gentle L2 decay per update
    for (let i = 0; i < this.inputDim; i++) {
      for (let j = 0; j < this.hiddenDim; j++) {
        const idx = i * this.hiddenDim + j;
        this.W_gen[idx] = this.W_gen[idx] * wdecay + wlr * error[i] * this.mu[j];
      }
    }

    // Constrain mu norm to prevent hidden state drift over long sessions
    const muNorm = norm(this.mu);
    if (muNorm > 3.0) {
      vscale(this.mu, 3.0 / muNorm, this.mu);
    }

    // Adapt precision: increase if errors are consistently small, decrease if large
    const relativeError = this.errorMagnitude / (this.errorBaseline + 1e-6);
    if (relativeError < 0.8) {
      this.precision = Math.min(3.0, this.precision * 1.001);
    } else if (relativeError > 1.5) {
      this.precision = Math.max(0.3, this.precision * 0.999);
    }
  }

  /** Get the hidden state (representation at this level) */
  getState(): Float32Array { return new Float32Array(this.mu); }

  /** Set hidden state (for loading saved state) */
  setState(state: Float32Array): void { this.mu.set(state); }

  /** Current prediction error magnitude */
  getErrorMagnitude(): number { return this.errorMagnitude; }

  /** Surprise: how much larger is current error vs baseline */
  getSurprise(): number {
    if (this.errorBaseline < 1e-6) return 0;
    return Math.max(0, this.errorMagnitude / this.errorBaseline - 1);
  }

  /** Get precision (inverse confidence) */
  getPrecision(): number { return this.precision; }
}

// ─── Predictive Coding Hierarchy ─────────────────────────────────────────────

export class PredictiveCodingHierarchy {
  private layers: PCLayer[];

  constructor(seed = 9999) {
    this.layers = LAYER_CONFIGS.map(([name, inputDim, hiddenDim], i) =>
      new PCLayer(name, inputDim, hiddenDim, seed + i * 10000)
    );
  }

  /**
   * Process an input signal through the full hierarchy.
   *
   * Signal flows bottom-up as prediction error.
   * Each layer predicts, computes error, updates, passes error up.
   *
   * Returns the hierarchical state: error magnitudes and surprise
   * at each level, plus the top-level arc representation.
   */
  process(input: Float32Array): PredictiveState {
    let signal = input;
    const errors: number[] = [];
    const surprises: number[] = [];

    for (const layer of this.layers) {
      // Top-down prediction
      const prediction = layer.predict();

      // Bottom-up error (with PCL suppression)
      const error = layer.computeError(signal, prediction);

      // Update internal model
      layer.update(error);

      errors.push(layer.getErrorMagnitude());
      surprises.push(layer.getSurprise());

      // Pass error up as the next level's input
      // Compress to next level's expected input dim
      signal = this.compress(error, layer.inputDim, this.getNextInputDim(layer));
    }

    // Read out the top-level arc state
    const arcState = this.layers[this.layers.length - 1].getState();

    return {
      errors,
      surprises,
      arcState,
      overallSurprise: this.computeOverallSurprise(surprises),
      isAnomaly: this.detectAnomaly(surprises),
      dominantLevel: this.findDominantErrorLevel(errors),
    };
  }

  /** Compress error from one dim to another (simple linear projection) */
  private compress(error: Float32Array, fromDim: number, toDim: number): Float32Array {
    if (toDim >= fromDim) {
      // Pad with zeros
      const out = zeros(toDim);
      for (let i = 0; i < fromDim; i++) out[i] = error[i];
      return out;
    }
    // Downsample by averaging blocks
    const out = zeros(toDim);
    const blockSize = fromDim / toDim;
    for (let i = 0; i < toDim; i++) {
      let sum = 0;
      const start = Math.floor(i * blockSize);
      const end = Math.floor((i + 1) * blockSize);
      for (let j = start; j < end && j < fromDim; j++) sum += error[j];
      out[i] = sum / (end - start);
    }
    return out;
  }

  /** Get the expected input dim for the layer above */
  private getNextInputDim(layer: PCLayer): number {
    const idx = this.layers.indexOf(layer);
    if (idx >= this.layers.length - 1) return layer.hiddenDim;
    return this.layers[idx + 1].inputDim;
  }

  /** Weighted overall surprise (higher levels weighted more) */
  private computeOverallSurprise(surprises: number[]): number {
    const weights = [0.1, 0.2, 0.3, 0.4]; // arc-level weighted most
    let sum = 0;
    for (let i = 0; i < surprises.length; i++) {
      sum += surprises[i] * (weights[i] ?? 0.25);
    }
    return sum;
  }

  /** Detect anomaly: surprise at session or arc level exceeds threshold */
  private detectAnomaly(surprises: number[]): boolean {
    return (surprises[2] ?? 0) > 2.0 || (surprises[3] ?? 0) > 1.5;
  }

  /** Which level has the most prediction error right now */
  private findDominantErrorLevel(errors: number[]): string {
    let maxIdx = 0;
    let maxVal = 0;
    for (let i = 0; i < errors.length; i++) {
      if (errors[i] > maxVal) { maxVal = errors[i]; maxIdx = i; }
    }
    return LAYER_CONFIGS[maxIdx][0];
  }

  // ─── Queries ───────────────────────────────────────────────────────────

  /** Get the 8-dim arc-level state (slowest-changing model of who the player is) */
  getArcState(): Float32Array {
    return this.layers[this.layers.length - 1].getState();
  }

  /** Get surprise at a specific level */
  getSurpriseAt(level: number): number {
    return this.layers[level]?.getSurprise() ?? 0;
  }

  /** Get layer names */
  getLayerNames(): string[] {
    return this.layers.map(l => l.name);
  }

  /** Is the session currently predictable (low overall surprise)? */
  isPredictable(): boolean {
    return this.layers.every(l => l.getSurprise() < 0.5);
  }

  /** Reset all layers (new session) */
  reset(): void {
    for (const layer of this.layers) {
      layer.setState(zeros(layer.hiddenDim));
    }
  }

  // ─── Serialization ─────────────────────────────────────────────────────

  serialize(): { states: number[][] } {
    return {
      states: this.layers.map(l => Array.from(l.getState())),
    };
  }

  loadState(data: { states: number[][] }): void {
    for (let i = 0; i < this.layers.length; i++) {
      if (data.states[i]) {
        this.layers[i].setState(new Float32Array(data.states[i]));
      }
    }
  }
}

// ─── Output Types ────────────────────────────────────────────────────────────

export interface PredictiveState {
  /** Prediction error magnitude at each level */
  errors: number[];
  /** Surprise (relative error vs baseline) at each level */
  surprises: number[];
  /** Top-level arc representation (8-dim model of player identity) */
  arcState: Float32Array;
  /** Weighted overall surprise */
  overallSurprise: number;
  /** Whether this constitutes an anomaly */
  isAnomaly: boolean;
  /** Which level is driving the most error right now */
  dominantLevel: string;
}
